import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  Paper,
  TextField,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import api from "../../api";
import AdConversionsCard from "../../components/admin/AdConversionsCard";
import { toast } from "react-toastify";
import { CURRENCY_UNITS } from "../../utils/answerUnitUtils";
import { DRIVER_LABELS, THEME_LABELS, TUTOR_LABELS } from "../ExplanationVideos/styleOptions";
import { StylePreview } from "../ExplanationVideos/StyleFields";

const PURPLE = "#7c3aed";

function StudentTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAnswerUnit, setShowAnswerUnit] = useState(true);
  const [mcqNoticeMessage, setMcqNoticeMessage] = useState("");
  const [mcqNoticeDefault, setMcqNoticeDefault] = useState("");
  const [defaultUnits, setDefaultUnits] = useState([]);
  const [customUnits, setCustomUnits] = useState([]);
  const [defaultCompounds, setDefaultCompounds] = useState([]);
  const [customCompounds, setCustomCompounds] = useState([]);
  const [newUnit, setNewUnit] = useState("");
  const [unitError, setUnitError] = useState("");
  const [newCompound, setNewCompound] = useState(["", ""]);
  const [compoundError, setCompoundError] = useState("");
  const [allowPaperDownload, setAllowPaperDownload] = useState(false);
  const [allowScanUpload, setAllowScanUpload] = useState(false);
  const [answersRequirePin, setAnswersRequirePin] = useState(true);
  const [showDashboard, setShowDashboard] = useState(true);
  const [showPracticeMode, setShowPracticeMode] = useState(true);
  const [clockTimeSeparator, setClockTimeSeparator] = useState("both");
  const [testAnswer, setTestAnswer] = useState("");
  const [testQuestion, setTestQuestion] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get("admin/analytics/student-settings")
      .then((res) => {
        const d = res.data?.data ?? {};
        setShowAnswerUnit(!!d.show_answer_unit);
        setAllowPaperDownload(d.allow_paper_download === true);
        setAllowScanUpload(d.allow_scan_upload === true);
        setAnswersRequirePin(d.answers_require_pin !== false);
        setShowDashboard(d.show_dashboard !== false);
        setShowPracticeMode(d.show_practice_mode !== false);
        setClockTimeSeparator(d.clock_time_separator === "colon" ? "colon" : "both");
        setMcqNoticeMessage(d.mcq_notice_message || "");
        setMcqNoticeDefault(d.mcq_notice_message_default || "");
        setDefaultUnits(Array.isArray(d.answer_units_default) ? d.answer_units_default : []);
        setCustomUnits(Array.isArray(d.answer_units_custom) ? d.answer_units_custom : []);
        setDefaultCompounds(Array.isArray(d.answer_unit_compounds_default) ? d.answer_unit_compounds_default : []);
        setCustomCompounds(Array.isArray(d.answer_unit_compounds_custom) ? d.answer_unit_compounds_custom : []);
      })
      .catch(() => toast.error("Failed to load student settings."))
      .finally(() => setLoading(false));
  }, []);

  const addUnit = () => {
    const value = newUnit.trim();
    if (!value) return;
    const known = [...defaultUnits, ...customUnits].map((u) => String(u).toLowerCase());
    if (known.includes(value.toLowerCase())) {
      setUnitError(`"${value}" is already in the list.`);
      return;
    }
    setCustomUnits((prev) => [...prev, value]);
    setNewUnit("");
    setUnitError("");
  };

  const addCompound = () => {
    const parts = newCompound.map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) {
      setCompoundError("A compound needs at least two units.");
      return;
    }
    const key = parts.join("|").toLowerCase();
    const known = [...defaultCompounds, ...customCompounds].map((c) => c.join("|").toLowerCase());
    if (known.includes(key)) {
      setCompoundError("That combination already exists.");
      return;
    }
    setCustomCompounds((prev) => [...prev, parts]);
    setNewCompound(["", ""]);
    setCompoundError("");
  };

  const runTest = async () => {
    if (!testAnswer.trim()) return;
    setTesting(true);
    try {
      const res = await api.post("admin/analytics/student-settings/test-answer-unit", {
        correct_answer_text: testAnswer,
        question_text: testQuestion.trim() || null,
      });
      setTestResult(res.data?.data ?? null);
    } catch {
      toast.error("Could not test that mark scheme.");
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put("admin/analytics/student-settings", {
        show_answer_unit: showAnswerUnit,
        mcq_notice_message: mcqNoticeMessage.trim() || null,
        answer_units_custom: customUnits,
        answer_unit_compounds_custom: customCompounds,
        allow_paper_download: allowPaperDownload,
        allow_scan_upload: allowScanUpload,
        answers_require_pin: answersRequirePin,
        show_dashboard: showDashboard,
        show_practice_mode: showPracticeMode,
        clock_time_separator: clockTimeSeparator,
      });
      const d = res.data?.data ?? {};
      if (d.mcq_notice_message) setMcqNoticeMessage(d.mcq_notice_message);
      if (Array.isArray(d.answer_units_custom)) setCustomUnits(d.answer_units_custom);
      if (Array.isArray(d.answer_unit_compounds_custom)) setCustomCompounds(d.answer_unit_compounds_custom);
      toast.success("Student settings saved.");
    } catch {
      toast.error("Failed to save student settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Student Portal Display
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls the Dashboard tab in the student portal sidebar — on desktop, on the mobile
            mini sidebar, and in the mobile menu. When on, the tab is shown to every student
            regardless of the <strong>student-portal-view-dashboard</strong> role permission.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={showDashboard}
                  onChange={(e) => setShowDashboard(e.target.checked)}
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Show the Dashboard tab to students
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {showDashboard
                      ? "Dashboard is the first tab and the portal landing page."
                      : "Dashboard is hidden; students land on the first section their role allows."}
                  </Typography>
                </Box>
              }
            />
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Practice Mode
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls the <strong>Practice</strong> toggle in the student and parent top bars.
            Practice mode is what unlocks <strong>Check answer</strong> on mock exams, past papers
            and worksheets, so turning this off also hides every Check answer button — leaving them
            would let a student already in practice mode keep grading answers with no way to switch
            back.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={showPracticeMode}
                  onChange={(e) => setShowPracticeMode(e.target.checked)}
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Show the Practice toggle to students and parents
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {showPracticeMode
                      ? "The toggle is visible and Check answer is available while it is on."
                      : "The toggle is hidden and Check answer is unavailable everywhere."}
                  </Typography>
                </Box>
              }
            />
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Time Answers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            How a student may write a clock time when the mark scheme writes it with a colon
            (e.g. <strong>5:52</strong>). Applies to Check answer and to marking after submission,
            on past papers, mock exams and worksheets. A mark scheme written with a dot
            (e.g. 12.15) is not affected — it may be a decimal rather than a time.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <RadioGroup value={clockTimeSeparator} onChange={(e) => setClockTimeSeparator(e.target.value)}>
              <FormControlLabel
                value="both"
                disabled={saving}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Accept 5:52 or 5.52</Typography>
                    <Typography variant="caption" color="text.secondary">A colon or a dot between hours and minutes.</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="colon"
                disabled={saving}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Only accept 5:52</Typography>
                    <Typography variant="caption" color="text.secondary">A time written with a dot (5.52) is marked wrong.</Typography>
                  </Box>
                }
              />
            </RadioGroup>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Answer Units
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When enabled, the expected unit (e.g. cm, kg, £) is shown next to the student&apos;s
            answer input for short-answer questions. The student types only the numeric value;
            the unit is appended automatically on submission. Units apply to Maths questions only
            — English, Verbal and Non-Verbal Reasoning answers never show a unit.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={showAnswerUnit}
                  onChange={(e) => setShowAnswerUnit(e.target.checked)}
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Show answer units to students
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {showAnswerUnit ? "Units are currently visible." : "Units are currently hidden."}
                  </Typography>
                </Box>
              }
            />
          )}

          {!loading && showAnswerUnit && (
            <Box sx={{ mt: 3 }}>
              <Accordion
                disableGutters
                elevation={0}
                sx={{ border: "1px solid #eef0f4", borderRadius: 2, "&:before": { display: "none" } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Built-in units ({defaultUnits.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                    Recognised automatically. These cannot be removed — grading strips the same list,
                    so deleting one would stop answers wearing it from being marked correctly.
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {defaultUnits.map((u) => (
                      <Chip key={u} label={u} size="small" variant="outlined" />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Typography variant="body2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                Custom units
              </Typography>
              {customUnits.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  None yet — add a unit below if a paper uses one the list does not recognise.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
                  {customUnits.map((u, i) => (
                    <Chip
                      key={`${u}-${i}`}
                      label={u}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onDelete={saving ? undefined : () => setCustomUnits((prev) => prev.filter((_, j) => j !== i))}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mt: 1.5 }}>
                <TextField
                  size="small"
                  label="Add a unit"
                  placeholder="e.g. furlongs"
                  value={newUnit}
                  onChange={(e) => { setNewUnit(e.target.value); setUnitError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUnit(); } }}
                  inputProps={{ maxLength: 32 }}
                  error={!!unitError}
                  helperText={unitError || "Written exactly as it appears at the end of the mark scheme."}
                  disabled={saving}
                  sx={{ maxWidth: 320 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addUnit}
                  disabled={saving || !newUnit.trim()}
                  sx={{ mt: 0.5, textTransform: "none" }}
                >
                  Add
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Compound units
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Answers that wear more than one unit (&quot;6 minutes 24 seconds&quot;, &quot;£22 60p&quot;,
                &quot;2 m 50 cm&quot;) get one numeric box per unit instead of a single box with one chip.
                A currency member (£, $, €, ¥) is shown <strong>before</strong> its box; every other unit
                after it. Each member also matches its abbreviations and singular form, so
                &quot;feet + inches&quot; covers &quot;5 ft 4 in&quot; too.
              </Typography>
              <Accordion
                disableGutters
                elevation={0}
                sx={{ border: "1px solid #eef0f4", borderRadius: 2, mb: 1.5, "&:before": { display: "none" } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Built-in combinations ({defaultCompounds.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {defaultCompounds.map((c) => (
                      <Chip key={c.join("|")} label={c.join("  +  ")} size="small" variant="outlined" />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
              <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                {customCompounds.map((c, i) => (
                  <Box key={`${c.join("|")}-${i}`} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip label={c.join("  +  ")} size="small" color="primary" variant="outlined" />
                    <IconButton
                      size="small"
                      disabled={saving}
                      onClick={() => setCustomCompounds((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                {newCompound.map((part, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {i > 0 && <Typography variant="body2" sx={{ fontWeight: 700 }}>+</Typography>}
                    <TextField
                      size="small"
                      placeholder={i === 0 ? "£ or metres" : "p or centimetres"}
                      value={part}
                      onChange={(e) => {
                        const next = [...newCompound];
                        next[i] = e.target.value;
                        setNewCompound(next);
                        setCompoundError("");
                      }}
                      inputProps={{ maxLength: 32 }}
                      disabled={saving}
                      sx={{ width: 130 }}
                    />
                  </Box>
                ))}
                {newCompound.length < 4 && (
                  <Button
                    size="small"
                    onClick={() => setNewCompound((prev) => [...prev, ""])}
                    disabled={saving}
                    sx={{ textTransform: "none" }}
                  >
                    + unit
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addCompound}
                  disabled={saving}
                  sx={{ textTransform: "none" }}
                >
                  Add compound
                </Button>
              </Box>
              {compoundError && (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                  {compoundError}
                </Typography>
              )}
            </Box>
          )}

          {!loading && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Test a mark scheme
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                The chip only appears next to a student&apos;s answer box, so a unit this list does not
                recognise is invisible from the admin side. Paste a mark scheme exactly as it is stored
                to see the chip it produces.
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  fullWidth
                  label="Mark scheme (correct answer)"
                  placeholder="e.g. 9 3/4 hours"
                  value={testAnswer}
                  onChange={(e) => { setTestAnswer(e.target.value); setTestResult(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runTest(); } }}
                  inputProps={{ maxLength: 2000 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Question text (optional)"
                  placeholder="Used only when the mark scheme itself carries no unit"
                  value={testQuestion}
                  onChange={(e) => { setTestQuestion(e.target.value); setTestResult(null); }}
                  inputProps={{ maxLength: 5000 }}
                />
                <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={runTest}
                    disabled={testing || !testAnswer.trim()}
                    startIcon={testing ? <CircularProgress size={14} /> : null}
                    sx={{ textTransform: "none" }}
                  >
                    {testing ? "Testing…" : "Test"}
                  </Button>
                </Box>
              </Stack>

              {testResult && (
                <Paper
                  variant="outlined"
                  sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "#fafafa" }}
                >
                  {testResult.compound_units ? (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Compound answer — one numeric box per unit:
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        {testResult.compound_units.map((u, i) => {
                          const isPrefix = CURRENCY_UNITS.includes(u);
                          const label = <Typography variant="body2" sx={{ fontWeight: 700 }}>{u}</Typography>;
                          return (
                            <Box key={`${u}-${i}`} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {isPrefix ? label : null}
                              <Box sx={{ px: 2, py: 0.75, border: "1px solid #cbd5e1", borderRadius: 1, bgcolor: "#fff", color: "#94a3b8" }}>
                                Your answer
                              </Box>
                              {isPrefix ? null : label}
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  ) : testResult.unit ? (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        The student sees{testResult.source === "question wording" ? " (unit inferred from the question wording, not the mark scheme)" : ""}:
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {testResult.prefix && (
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{testResult.unit}</Typography>
                        )}
                        <Box sx={{ px: 2, py: 0.75, border: "1px solid #cbd5e1", borderRadius: 1, bgcolor: "#fff", color: "#94a3b8" }}>
                          Your answer
                        </Box>
                        {!testResult.prefix && (
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{testResult.unit}</Typography>
                        )}
                      </Box>
                    </>
                  ) : (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                      No unit chip — the student gets a plain box and must type the unit themselves.
                      {testResult.unrecognised_suffix
                        ? ` The mark scheme ends in "${testResult.unrecognised_suffix}", which is not in the list above.`
                        : ""}
                    </Alert>
                  )}

                  {testResult.unrecognised_suffix && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      sx={{ mt: 1, textTransform: "none" }}
                      onClick={() => {
                        setNewUnit(testResult.unrecognised_suffix);
                        setUnitError("");
                      }}
                    >
                      Add &quot;{testResult.unrecognised_suffix}&quot; as a custom unit
                    </Button>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                    Read as: {testResult.plain || "(empty)"}
                  </Typography>
                  {!testResult.enabled && (
                    <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
                      Units are switched off above, so nothing is shown to students right now.
                    </Typography>
                  )}
                </Paper>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Written Papers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls the Download and Upload buttons on the student Past Papers and Mock Exams
            cards, for students who prefer to work on paper. Turning either off hides the button
            and blocks the endpoint.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowPaperDownload}
                    onChange={(e) => setAllowPaperDownload(e.target.checked)}
                    disabled={saving}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Allow students to download papers
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {allowPaperDownload
                        ? "Students can download a paper to print. Serves the original PDF where one is stored, otherwise a generated question paper. Answer keys are never included."
                        : "The download button is hidden and downloads are blocked."}
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={answersRequirePin}
                    onChange={(e) => setAnswersRequirePin(e.target.checked)}
                    disabled={saving}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Answers need the parent PIN
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {answersRequirePin
                        ? "On My Purchases a student downloads answers only after a parent enters the parent PIN."
                        : "Students download the answers to papers they own without a PIN."}
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={allowScanUpload}
                    onChange={(e) => setAllowScanUpload(e.target.checked)}
                    disabled={saving}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Allow students to upload written papers for marking
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {allowScanUpload
                        ? "Students can photograph a paper they wrote by hand and have it marked. The result is recorded as a normal attempt."
                        : "The upload button is hidden and uploads are blocked."}
                    </Typography>
                  </Box>
                }
              />

              {allowScanUpload && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Each upload costs several AI vision calls plus marking. Drawing, matching and
                  ordering questions cannot be read from a scan — they are reported to the student
                  and score 0. Requires a worker on the <strong>scan-grading</strong> queue.
                </Alert>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Convert-to-MCQ Notice
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Message shown in the popup when a student first opens a paper or mock exam containing
            questions flagged as &quot;Recommend convert to MCQ&quot; (hard to answer online). The
            list of flagged question numbers is added below this message automatically. Leave empty
            to use the default message.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Popup message"
                value={mcqNoticeMessage}
                onChange={(e) => setMcqNoticeMessage(e.target.value)}
                inputProps={{ maxLength: 2000 }}
                disabled={saving}
                placeholder={mcqNoticeDefault}
              />
              {mcqNoticeDefault && mcqNoticeMessage.trim() !== mcqNoticeDefault && (
                <Button
                  size="small"
                  sx={{ mt: 1, textTransform: "none" }}
                  onClick={() => setMcqNoticeMessage(mcqNoticeDefault)}
                  disabled={saving}
                >
                  Reset to default message
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
        Changes take effect immediately for new page loads — no server restart needed.
      </Alert>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading || saving}
          sx={{ fontWeight: 700 }}
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </Box>
    </Box>
  );
}

function ParentTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowAnswerKeyDownload, setAllowAnswerKeyDownload] = useState(true);

  useEffect(() => {
    api.get("admin/analytics/parent-settings")
      .then((res) => setAllowAnswerKeyDownload(!!res.data?.data?.allow_answer_key_download))
      .catch(() => toast.error("Failed to load parent settings."))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("admin/analytics/parent-settings", {
        allow_answer_key_download: allowAnswerKeyDownload,
      });
      toast.success("Parent settings saved.");
    } catch {
      toast.error("Failed to save parent settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Answer Key Downloads
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When enabled, parents can download answer keys for past papers, mock exams, and worksheets
            (the &quot;With Answers&quot; worksheet download).
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={allowAnswerKeyDownload}
                  onChange={(e) => setAllowAnswerKeyDownload(e.target.checked)}
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Allow parents to download answer keys
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {allowAnswerKeyDownload ? "Parents can download answer keys." : "Answer key downloads are hidden and blocked."}
                  </Typography>
                </Box>
              }
            />
          )}
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
        Changes take effect immediately for new page loads — no server restart needed.
      </Alert>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading || saving}
          sx={{ fontWeight: 700 }}
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </Box>
    </Box>
  );
}

function SystemTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCoursesMenu, setShowCoursesMenu] = useState(false);
  const [googleSignIn, setGoogleSignIn] = useState(true);
  const [appleSignIn, setAppleSignIn] = useState(true);
  const [showPastPapersList, setShowPastPapersList] = useState(true);
  const [schoolBundleSort, setSchoolBundleSort] = useState("rank");
  const [defaultSchoolCategory, setDefaultSchoolCategory] = useState("independent");
  // One-page checkout for papers, mock exams and packs (off = classic checkout everywhere).
  const [checkoutOnePage, setCheckoutOnePage] = useState(false);
  // Per-section switches for /practice-papers (all on by default).
  const [pastPaperSections, setPastPaperSections] = useState({
    show_all_papers_bundle: true,
    show_free_papers: true,
    show_paid_papers: true,
    show_all_mock_exams_bundle: true,
  });
  const [graceDays, setGraceDays] = useState(7);
  // What a student, parent or buyer needs before a paper / mock exam / worksheet is shown.
  const [visibility, setVisibility] = useState({
    paper: "fully_verified",
    mock_exam: "fully_verified",
    worksheet: "fully_verified",
  });

  useEffect(() => {
    api.get("admin/analytics/system-settings")
      .then((res) => {
        setShowCoursesMenu(!!res.data?.data?.show_courses_menu);
        setGoogleSignIn(res.data?.data?.google_sign_in === true);
        // Older API without the Apple switch: Apple followed Google.
        setAppleSignIn(
          res.data?.data?.apple_sign_in === undefined
            ? res.data?.data?.google_sign_in === true
            : res.data.data.apple_sign_in === true
        );
        setShowPastPapersList(res.data?.data?.show_past_papers_list !== false);
        setSchoolBundleSort(res.data?.data?.school_bundle_sort === "pupils" ? "pupils" : "rank");
        setDefaultSchoolCategory(
          ["grammar", "both", "all"].includes(res.data?.data?.default_school_category)
            ? res.data.data.default_school_category
            : "independent"
        );
        setCheckoutOnePage(res.data?.data?.checkout_one_page === true);
        setPastPaperSections({
          show_all_papers_bundle: res.data?.data?.show_all_papers_bundle !== false,
          show_free_papers: res.data?.data?.show_free_papers !== false,
          show_paid_papers: res.data?.data?.show_paid_papers !== false,
          show_all_mock_exams_bundle: res.data?.data?.show_all_mock_exams_bundle !== false,
        });
        setGraceDays(Number(res.data?.data?.payment_grace_days) || 7);
        setVisibility({
          paper: res.data?.data?.paper_visibility || "fully_verified",
          mock_exam: res.data?.data?.mock_exam_visibility || "fully_verified",
          worksheet: res.data?.data?.worksheet_visibility || "fully_verified",
        });
      })
      .catch(() => toast.error("Failed to load system settings."))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("admin/analytics/system-settings", {
        show_courses_menu: showCoursesMenu,
        google_sign_in: googleSignIn,
        apple_sign_in: appleSignIn,
        show_past_papers_list: showPastPapersList,
        school_bundle_sort: schoolBundleSort,
        default_school_category: defaultSchoolCategory,
        checkout_one_page: checkoutOnePage,
        ...pastPaperSections,
        paper_visibility: visibility.paper,
        mock_exam_visibility: visibility.mock_exam,
        worksheet_visibility: visibility.worksheet,
        payment_grace_days: Math.max(1, Math.min(60, Number(graceDays) || 7)),
      });
      toast.success("System settings saved.");
    } catch {
      toast.error("Failed to save system settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Public Site Menu
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls whether <strong>Courses</strong> appears in the main menu on the public site.
            Turn this on once courses and revision products are ready to sell — while it is off,
            visitors cannot browse to the catalogue and book something nobody is running yet.
            Courses stay reachable by direct link either way, so you can keep testing.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={showCoursesMenu}
                  onChange={(e) => setShowCoursesMenu(e.target.checked)}
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Show &quot;Courses&quot; in the public site menu
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {showCoursesMenu
                      ? "Visitors can browse courses and revision products from the menu."
                      : "The menu entry is hidden. Courses are still reachable by direct link."}
                  </Typography>
                </Box>
              }
            />
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Public sign-in
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Email and password always stay on the login and sign-up pages. These switches
            show or hide Continue with Google and Sign in with Apple, each on its own, on the
            public site (sign-up, login, checkout), the student-portal login and the app sign-in.
            Admin and tutor login are not affected.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={googleSignIn}
                    onChange={(e) => setGoogleSignIn(e.target.checked)}
                    disabled={saving}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Allow Google sign-in
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {googleSignIn
                        ? "Continue with Google is shown next to the email form."
                        : "Continue with Google is hidden."}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={appleSignIn}
                    onChange={(e) => setAppleSignIn(e.target.checked)}
                    disabled={saving}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Allow Apple sign-in
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {appleSignIn
                        ? "Sign in with Apple is shown next to the email form."
                        : "Sign in with Apple is hidden."}
                      {googleSignIn && !appleSignIn
                        ? " Note: the iOS app must offer Apple whenever it offers Google (App Store rule)."
                        : ""}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <AdConversionsCard />

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Checkout
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The one-page checkout takes details and card on one page with no emailed code, for papers, mock exams
            and packs. Baskets with a course always use the classic checkout. Test before switching on with{" "}
            <strong>/checkout?checkout=new</strong>; <strong>?checkout=classic</strong> always opens the old one.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={checkoutOnePage}
                onChange={(e) => setCheckoutOnePage(e.target.checked)}
                disabled={loading || saving}
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Use the one-page checkout
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {checkoutOnePage
                    ? "On: papers, mock exams and packs pay on one page. Turn off to go back to the classic checkout."
                    : "Off: everyone uses the classic checkout (email code, then Stripe's payment box)."}
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Practice Papers Page
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls what <strong>/practice-papers</strong> shows. The bundle packs (Papers → Bundle Packs) always
            show. Turn this off to hide the individual past papers tables so visitors see only the packs.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {[
            {
              key: "list",
              checked: showPastPapersList,
              onChange: setShowPastPapersList,
              label: "Show individual practice papers on /practice-papers",
              help: showPastPapersList
                ? "Master switch. When off, only the bundle packs are shown and the switches below have no effect."
                : "Only the bundle packs are shown.",
            },
            {
              key: "show_all_papers_bundle",
              label: "Show the “All Practice Papers” bundle",
              help: "The one-payment offer for every practice paper: the banner above the tables and the “Add more for less” card in the basket.",
            },
            {
              key: "show_free_papers",
              label: "Show the “FREE Solved Practice Papers” table",
              help: "Free papers with a free PDF and free answers.",
            },
            {
              key: "show_paid_papers",
              label: "Show the “Unlock the papers below” table",
              help: "Paid papers with Download, Answers and Add to Cart. “Bought by you” always shows so buyers can download.",
            },
          ].map((row) => {
            const isMaster = row.key === "list";
            const checked = isMaster ? row.checked : pastPaperSections[row.key];
            return (
              <FormControlLabel
                key={row.key}
                sx={{ display: "flex", alignItems: "flex-start", mb: 1.5, ml: isMaster ? 0 : 3 }}
                control={
                  <Switch
                    checked={checked}
                    onChange={(e) =>
                      isMaster
                        ? row.onChange(e.target.checked)
                        : setPastPaperSections((prev) => ({ ...prev, [row.key]: e.target.checked }))
                    }
                    disabled={loading || saving || (!isMaster && !showPastPapersList)}
                  />
                }
                label={
                  <Box sx={{ pt: 0.75 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {row.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.help}
                    </Typography>
                  </Box>
                }
              />
            );
          })}
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            School bundle order
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            How individual school packs are ordered inside the top London bundle and the other
            practice-paper and mock-exam lists. Rank follows Admin → Display order. Most pupils
            sitting puts schools with the most students targeting them first.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <RadioGroup
            value={schoolBundleSort}
            onChange={(e) => setSchoolBundleSort(e.target.value)}
          >
            <FormControlLabel
              value="rank"
              disabled={loading || saving}
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Rank</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Areas, consortiums and schools stay in the order set on Display order.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="pupils"
              disabled={loading || saving}
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Most pupils sitting</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Schools with the most students listing them as a target come first.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
          <Divider sx={{ my: 2 }} />
          <TextField
            select
            size="small"
            label="Default school type"
            value={defaultSchoolCategory}
            disabled={loading || saving}
            onChange={(e) => setDefaultSchoolCategory(e.target.value)}
            sx={{ minWidth: 240 }}
            helperText="Practice Papers, Past Papers and Mock Exams open on this type when the link does not set one."
          >
            <MenuItem value="independent">Independent</MenuItem>
            <MenuItem value="grammar">Grammar</MenuItem>
            <MenuItem value="both">Both</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Mock Exams Page
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls the <strong>All Mock Exams</strong> pack on <strong>/mock-exams</strong> and in the basket.
            Anyone who already bought it keeps their access.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControlLabel
            sx={{ display: "flex", alignItems: "flex-start" }}
            control={
              <Switch
                checked={pastPaperSections.show_all_mock_exams_bundle}
                onChange={(e) =>
                  setPastPaperSections((prev) => ({ ...prev, show_all_mock_exams_bundle: e.target.checked }))
                }
                disabled={loading || saving}
              />
            }
            label={
              <Box sx={{ pt: 0.75 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Show the “All Mock Exams” pack
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The one-payment offer for every mock exam: the banner above the list and the “Add more for less” card in the basket.
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Student Visibility
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            What a practice paper, mock exam or worksheet needs before <strong>students, parents and
            buyers</strong> (including the public site) can see, buy or start it. Testers, admins and
            verifiers always see everything, including inactive items.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={2}>
            {[
              { type: "paper", label: "Practice papers" },
              { type: "mock_exam", label: "Mock exams" },
              { type: "worksheet", label: "Worksheets" },
            ].map(({ type, label }) => (
              <TextField
                key={type}
                select
                fullWidth
                size="small"
                label={label}
                value={visibility[type]}
                onChange={(e) => setVisibility((prev) => ({ ...prev, [type]: e.target.value }))}
                disabled={loading || saving}
              >
                <MenuItem value="active">Active only</MenuItem>
                <MenuItem value="signed_off">Active + signed off (whole-item approval)</MenuItem>
                <MenuItem value="fully_verified">Active + fully verified on web (Verification Dashboard)</MenuItem>
              </TextField>
            ))}
          </Stack>
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Fully verified</strong> is the green “Fully verified” on the Verification Dashboard with
            Platform = <strong>Web</strong>: the whole item approved on web, or every question verified on web.
            Mobile verification is not needed for the web portal. Check the count there (Platform: Web)
            before choosing it, because students see only those.
          </Alert>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Failed Payment Grace Period
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            After a Mobile Plan payment fails, the subscriber keeps their original price for this
            many days if they update their card. After that they are offered the current price.
            The payment-failed email quotes this deadline, and one reminder is sent the day before
            it ends.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField
            type="number"
            label="Grace period (days)"
            size="small"
            value={graceDays}
            onChange={(e) => setGraceDays(e.target.value)}
            inputProps={{ min: 1, max: 60 }}
            disabled={loading || saving}
            helperText="Between 1 and 60. Default 7; 14 or 30 are common."
          />
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
        Changes take effect immediately for new page loads — no server restart needed.
      </Alert>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading || saving}
          sx={{ fontWeight: 700 }}
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </Box>
    </Box>
  );
}

function PlaceholderTab({ label }) {
  return (
    <Box sx={{ maxWidth: 640 }}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {label} settings are not yet configured.
      </Alert>
    </Box>
  );
}

/* ───────────────────────── Videos tab: presenter and narration voice ───────────────────────── */

const VIDEO_VISIBILITY_OPTIONS = [
  { value: "all", label: "All finished videos", help: "Every finished video that passed the automatic check." },
  { value: "approved", label: "Only published videos", help: "A video stays hidden until an admin publishes it on the Explanation Videos page." },
  { value: "none", label: "No videos", help: "Hide every explanation video from students and parents." },
];

// Who sees explanation videos. Admins always see every video on the Explanation Videos page.
const VideoVisibilityCard = () => {
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("admin/explanation-videos/settings")
      .then((res) => setValue(res.data?.data?.visibility || "all"))
      .catch(() => setValue("all"));
  }, []);

  const change = async (e) => {
    const next = e.target.value;
    const previous = value;
    setValue(next);
    setSaving(true);
    try {
      await api.put("admin/explanation-videos/settings", { visibility: next });
      toast.success("Video visibility saved.");
    } catch (err) {
      setValue(previous);
      toast.error(err.response?.data?.message || "Failed to save video visibility.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", mb: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Who can watch explanation videos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Applies to students and parents on worksheets, mock exams and practice papers. Admins can always watch
          every video to review it.
        </Typography>
        {value === null ? (
          <CircularProgress size={18} />
        ) : (
          <TextField select size="small" fullWidth label="Students & parents see" value={value} onChange={change}
            disabled={saving} helperText={VIDEO_VISIBILITY_OPTIONS.find((o) => o.value === value)?.help}>
            {VIDEO_VISIBILITY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        )}
      </CardContent>
    </Card>
  );
};

// Grok Imagine background art for one board design: made once (~$0.02), kept on the server.
const ThemeArtPanel = ({ theme, grokConfigured, onChanged }) => {
  const [art, setArt] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setArt(null);
    api.get(`admin/analytics/video-settings/theme-art/${theme}`)
      .then((res) => {
        if (!alive) return;
        setArt(res.data?.data?.image || null);
        setPrompt(res.data?.data?.default_prompt || "");
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [theme]);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post("admin/analytics/video-settings/theme-art", { theme, prompt });
      setArt(res.data?.data?.image || null);
      onChanged(true);
      toast.success(res.data?.message || "Background made.");
    } catch (e) {
      toast.error(e.response?.data?.message || "Grok Imagine did not return an image.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`admin/analytics/video-settings/theme-art/${theme}`);
      setArt(null);
      onChanged(false);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not remove the background.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ border: "1px dashed #d1d5db", borderRadius: 2, p: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Wall art for this design (optional)</Typography>
      {art ? (
        <Box component="img" src={art} alt="" sx={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 1, mb: 1 }} />
      ) : (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Plain colours. Grok Imagine can make a soft background for the wall, once, for about $0.02.
        </Typography>
      )}
      <TextField size="small" fullWidth multiline minRows={2} label="Prompt" value={prompt}
        onChange={(e) => setPrompt(e.target.value)} inputProps={{ maxLength: 600 }} sx={{ mb: 1 }} />
      <Stack direction="row" spacing={1} alignItems="center">
        <Button size="small" variant="outlined" onClick={generate} disabled={busy || !grokConfigured}>
          {art ? "Make a new one with Grok (~$0.02)" : "Make with Grok (~$0.02)"}
        </Button>
        {art && <Button size="small" color="error" onClick={remove} disabled={busy}>Use plain colours</Button>}
        {busy && <CircularProgress size={18} />}
      </Stack>
      {!grokConfigured && (
        <Typography variant="caption" color="text.secondary">Needs XAI_API_KEY in the backend .env.</Typography>
      )}
    </Box>
  );
};

const VideosTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [form, setForm] = useState(null);
  const [options, setOptions] = useState({
    drivers: [],
    openai_models: [],
    openai_voices: [],
    openai_female_voices: [],
    openai_male_voices: [],
    azure_voices: [],
    gemini_female_voices: [],
    gemini_male_voices: [],
    grok_female_voices: [],
    grok_male_voices: [],
    google_female_voices: [],
    google_male_voices: [],
    board_themes: [],
    music_tracks: [],
    tutors: [],
  });
  const [azure, setAzure] = useState({ configured: false, region: "" });
  const [gemini, setGemini] = useState({ configured: false, model: "" });
  const [grokConfigured, setGrokConfigured] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(false);
  const [themeArt, setThemeArt] = useState([]);
  const [musicPath, setMusicPath] = useState("");

  useEffect(() => {
    api.get("admin/analytics/video-settings")
      .then((res) => {
        const d = res.data?.data || {};
        setForm({
          driver: d.driver || "google",
          openai_model: d.openai_model || "",
          openai_voice: d.openai_voice || "auto",
          openai_voice_female: d.openai_voice_female || "auto",
          openai_voice_male: d.openai_voice_male || "ash",
          azure_voice: d.azure_voice || "",
          azure_voice_male: d.azure_voice_male || "en-GB-RyanNeural",
          gemini_voice_female: d.gemini_voice_female || "Kore",
          gemini_voice_male: d.gemini_voice_male || "Puck",
          grok_voice_female: d.grok_voice_female || "ara",
          grok_voice_male: d.grok_voice_male || "rex",
          google_voice_female: d.google_voice_female || "en-GB-Wavenet-F",
          google_voice_male: d.google_voice_male || "en-GB-Wavenet-B",
          board_theme: d.board_theme || "classic",
          instructions: d.instructions || "",
          rate: d.rate ?? 1,
          tutor: d.tutor || "mix",
          music_enabled: !!d.music_enabled,
          music_track: d.music_track || "auto",
          music_volume: d.music_volume ?? -22,
        });
        setOptions((o) => ({ ...o, ...d.options }));
        setAzure({ configured: !!d.azure_configured, region: d.azure_region || "" });
        setGemini({ configured: !!d.gemini_configured, model: d.gemini_model || "" });
        setMusicPath(d.music_path || "");
        setGrokConfigured(!!d.grok_configured);
        setGoogleConfigured(!!d.google_configured);
        setThemeArt(d.theme_art || []);
      })
      .catch(() => toast.error("Failed to load video settings."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("admin/analytics/video-settings", {
        ...form,
        rate: Number(form.rate) || 1,
        music_volume: Number(form.music_volume) || -22,
      });
      toast.success("Video settings saved. New videos use them from now on.");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save video settings.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Preview plays the saved voice for a girl or boy presenter, so save first.
  const preview = async (tutor) => {
    if (!(await save())) return;
    setPreviewing(true);
    setPreviewAudio(null);
    try {
      const res = await api.post("admin/analytics/video-settings/preview", { tutor, subject: "maths" });
      setPreviewAudio(res.data?.data?.audio || null);
    } catch (e) {
      toast.error(e.response?.data?.message || "The voice service did not return audio.");
    } finally {
      setPreviewing(false);
    }
  };

  if (loading || !form) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  const azureChosenButMissing = form.driver === "azure" && !azure.configured;

  return (
    <Box sx={{ maxWidth: 640 }}>
      <VideoVisibilityCard />
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Explanation video presenter and voice
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The voice that reads the whiteboard explanation videos. Changes apply to videos made or
            re-made after you save; existing videos keep their voice.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            {/* What new videos will look like; follows the presenter and design chosen below. */}
            <StylePreview tutor={form.tutor} theme={form.board_theme} width={360} />
            <TextField select size="small" label="Presenter" value={form.tutor} onChange={set("tutor")}
              helperText="Who presents on the board. The 3D characters are animated in step with the video.">
              {(options.tutors || []).map((t) => (
                <MenuItem key={t} value={t}>{TUTOR_LABELS[t] || t}</MenuItem>
              ))}
            </TextField>

            <TextField select size="small" label="Voice provider" value={form.driver} onChange={set("driver")}
              helperText="Any provider without its key, or that fails, falls back to OpenAI.">
              {(options.drivers || []).map((d) => (
                <MenuItem key={d} value={d}>{DRIVER_LABELS[d] || d}</MenuItem>
              ))}
            </TextField>

            {form.driver === "grok" && (
              <Alert severity={grokConfigured ? "success" : "warning"}>
                {grokConfigured
                  ? "xAI key configured. About $4.20 per million characters, roughly a quarter of OpenAI."
                  : "xAI key not configured. Add XAI_API_KEY to the backend .env — until then videos use OpenAI."}
              </Alert>
            )}
            {form.driver === "grok" && (
              <Stack direction="row" spacing={1}>
                <TextField select size="small" fullWidth label="Grok voice for girls" value={form.grok_voice_female}
                  onChange={set("grok_voice_female")} helperText="The robot speaks with sal.">
                  {options.grok_female_voices.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </TextField>
                <TextField select size="small" fullWidth label="Grok voice for boys" value={form.grok_voice_male}
                  onChange={set("grok_voice_male")}>
                  {options.grok_male_voices.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </TextField>
              </Stack>
            )}

            {form.driver === "google" && (
              <Alert severity={googleConfigured ? "success" : "warning"}>
                {googleConfigured
                  ? "Google key configured. 4 million WaveNet characters a month are free, then $4 per million."
                  : "Google key not configured. Add GOOGLE_TTS_API_KEY (Text-to-Speech API enabled) to the backend .env — until then videos use OpenAI."}
              </Alert>
            )}
            {form.driver === "google" && (
              <Stack direction="row" spacing={1}>
                <TextField select size="small" fullWidth label="WaveNet voice for girls and the robot" value={form.google_voice_female}
                  onChange={set("google_voice_female")}>
                  {options.google_female_voices.map((v) => <MenuItem key={v} value={v}>{v.replace("en-GB-", "")}</MenuItem>)}
                </TextField>
                <TextField select size="small" fullWidth label="WaveNet voice for boys" value={form.google_voice_male}
                  onChange={set("google_voice_male")}>
                  {options.google_male_voices.map((v) => <MenuItem key={v} value={v}>{v.replace("en-GB-", "")}</MenuItem>)}
                </TextField>
              </Stack>
            )}

            {form.driver === "gemini" && (
              <Alert severity={gemini.configured ? "success" : "warning"}>
                {gemini.configured
                  ? `Gemini key configured (model ${gemini.model}). Same price as OpenAI, about $0.015 a minute.`
                  : "Gemini key not configured. Add GEMINI_API_KEY to the backend .env — until then videos use OpenAI."}
              </Alert>
            )}

            {form.driver === "azure" && (
              <Alert severity={azure.configured ? "success" : "warning"}>
                {azure.configured
                  ? `Azure key configured (region ${azure.region}).`
                  : "Azure key not configured. Add AZURE_SPEECH_KEY to the backend .env — until then videos use OpenAI."}
              </Alert>
            )}

            {form.driver === "gemini" && (
              <>
                <TextField select size="small" label="Gemini voice for girl presenters and the robot" value={form.gemini_voice_female}
                  onChange={set("gemini_voice_female")}>
                  {options.gemini_female_voices.map((v) => (
                    <MenuItem key={v} value={v}>{v}</MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" label="Gemini voice for boy presenters" value={form.gemini_voice_male}
                  onChange={set("gemini_voice_male")} helperText="Fumiriya, Sam, Leo.">
                  {options.gemini_male_voices.map((v) => (
                    <MenuItem key={v} value={v}>{v}</MenuItem>
                  ))}
                </TextField>
                <TextField size="small" label="Speaking style" value={form.instructions} onChange={set("instructions")}
                  multiline minRows={2} inputProps={{ maxLength: 500 }}
                  helperText="How the voice should sound. Gemini follows it closely." />
              </>
            )}

            {form.driver === "openai" || azureChosenButMissing || (form.driver === "gemini" && !gemini.configured)
              || (form.driver === "grok" && !grokConfigured) || (form.driver === "google" && !googleConfigured) ? (
              <>
                <TextField select size="small" label="OpenAI model" value={form.openai_model} onChange={set("openai_model")}>
                  {options.openai_models.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" label="OpenAI voice for girl presenters" value={form.openai_voice_female}
                  onChange={set("openai_voice_female")}
                  helperText="Shino, Shibu, Bibi, Vita, Victoria, Maya, Ava. Only female voices; Auto picks one per subject.">
                  {["auto", ...options.openai_female_voices].map((v) => (
                    <MenuItem key={v} value={v}>{v === "auto" ? "Auto (female voice per subject)" : v}</MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" label="OpenAI voice for boy presenters" value={form.openai_voice_male}
                  onChange={set("openai_voice_male")}
                  helperText="Fumiriya, Sam, Leo. Only male voices; Auto picks one per subject.">
                  {["auto", ...options.openai_male_voices].map((v) => (
                    <MenuItem key={v} value={v}>{v === "auto" ? "Auto (male voice per subject)" : v}</MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" label="OpenAI voice for the robot" value={form.openai_voice} onChange={set("openai_voice")}
                  helperText="The robot has no gender. Auto uses a different voice per subject.">
                  {options.openai_voices.map((v) => (
                    <MenuItem key={v} value={v}>{v === "auto" ? "Auto (per subject)" : v}</MenuItem>
                  ))}
                </TextField>
                <TextField size="small" label="Speaking style" value={form.instructions} onChange={set("instructions")}
                  multiline minRows={2} inputProps={{ maxLength: 500 }}
                  helperText="How the voice should sound. Used by the gpt-4o TTS models only." />
              </>
            ) : null}

            {form.driver === "azure" && (
              <>
                <TextField select size="small" label="Azure voice for girl presenters and the robot" value={form.azure_voice}
                  onChange={set("azure_voice")} helperText="Pick a female voice (Sonia, Libby, Maisie, Ada).">
                  {options.azure_voices.map((v) => (
                    <MenuItem key={v} value={v}>{v.replace("en-GB-", "").replace("Neural", "")} ({v})</MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" label="Azure voice for boy presenters" value={form.azure_voice_male}
                  onChange={set("azure_voice_male")} helperText="Used when the presenter is a boy (Fumiriya, Sam, Leo).">
                  {options.azure_voices.map((v) => (
                    <MenuItem key={v} value={v}>{v.replace("en-GB-", "").replace("Neural", "")} ({v})</MenuItem>
                  ))}
                </TextField>
              </>
            )}

            <TextField size="small" type="number" label="Speed" value={form.rate} onChange={set("rate")}
              inputProps={{ min: 0.7, max: 1.3, step: 0.05 }}
              helperText="1 is normal. Azure only; slower suits younger children." />

            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Board design</Typography>
            <TextField select size="small" label="Design" value={form.board_theme} onChange={set("board_theme")}
              helperText="The classroom wall, board frame and step cards. The writing area stays light in every design.">
              {(options.board_themes || []).map((t) => (
                <MenuItem key={t} value={t}>{THEME_LABELS[t] || t}{themeArt.includes(t) ? " · with Grok art" : ""}</MenuItem>
              ))}
            </TextField>
            <ThemeArtPanel theme={form.board_theme} grokConfigured={grokConfigured}
              onChanged={(has) => setThemeArt((list) => (has ? [...new Set([...list, form.board_theme])] : list.filter((t) => t !== form.board_theme)))} />

            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Background music</Typography>
            <FormControlLabel
              control={
                <Switch checked={form.music_enabled}
                  onChange={(e) => setForm((f) => ({ ...f, music_enabled: e.target.checked }))} />
              }
              label="Play quiet music under the voice"
            />
            {form.music_enabled && (
              options.music_tracks.length === 0 ? (
                <Alert severity="warning">
                  No music tracks yet. Put licensed mp3/m4a/wav/ogg files in {musicPath || "storage/app/whiteboard-music"} on the server.
                </Alert>
              ) : (
                <>
                  <TextField select size="small" label="Track" value={form.music_track} onChange={set("music_track")}
                    helperText="Auto gives each question its own track from the folder.">
                    {["auto", ...options.music_tracks].map((t) => (
                      <MenuItem key={t} value={t}>{t === "auto" ? "Auto (one per question)" : t}</MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" type="number" label="Music volume (dB)" value={form.music_volume}
                    onChange={set("music_volume")} inputProps={{ min: -40, max: -10, step: 1 }}
                    helperText="−22 is a quiet bed. The music dips further under every spoken word." />
                </>
              )
            )}

            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving || previewing}
                sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: "#6d28d9" } }}>
                Save
              </Button>
              <Button variant="outlined" onClick={() => preview("shino")} disabled={saving || previewing}>
                Save & hear girl voice
              </Button>
              <Button variant="outlined" onClick={() => preview("fumiriya")} disabled={saving || previewing}>
                Save & hear boy voice
              </Button>
              {previewing && <CircularProgress size={18} />}
            </Stack>
            {previewAudio && <audio controls autoPlay src={previewAudio} style={{ width: "100%" }} />}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

const TABS = ["Student", "Parent", "Tutor", "Tester", "System", "Videos"];

const AdminSettings = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Platform-wide configuration settings.
        </Typography>
      </Box>

      <Paper
        sx={{
          mb: 3,
          borderRadius: 3,
          border: "1px solid #eef0f4",
          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            px: { xs: 0.5, sm: 1 },
            bgcolor: "#fafafa",
            borderBottom: "1px solid #eef0f4",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              minHeight: 48,
              color: "#64748b",
            },
            "& .Mui-selected": { color: PURPLE },
            "& .MuiTabs-indicator": { backgroundColor: PURPLE, height: 3 },
          }}
        >
          {TABS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tab === 0 && <StudentTab />}
          {tab === 1 && <ParentTab />}
          {tab === 2 && <PlaceholderTab label="Tutor" />}
          {tab === 3 && <PlaceholderTab label="Tester" />}
          {tab === 4 && <SystemTab />}
          {tab === 5 && <VideosTab />}
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminSettings;
